import { useState } from 'react';
import { Link } from 'react-router-dom';
import Ikon from './Ikon';

/*
 * Potongan kecil yang berulang di semua halaman publik desain Figma. Ukuran dan
 * warna diambil langsung dari berkas Figma (frame 1440px, isi selebar 1280px
 * dengan gutter 80px).
 */

/* Pembungkus isi: 1280px di desktop, gutter menyempit di layar kecil. */
export function Wadah({ className = '', children }) {
    return <div className={`mx-auto w-full max-w-[1440px] px-4 sm:px-8 lg:px-20 ${className}`}>{children}</div>;
}

/* Label kecil kapital di atas judul seksi, mis. "BERITA & ARTIKEL". */
export function LabelSeksi({ children, gelap = false, oranye = false, className = '' }) {
    const warna = oranye ? 'text-o-500' : gelap ? 'text-pb-limau' : 'text-pb-aksen';
    return (
        <p className={`font-body text-xs font-semibold uppercase tracking-[1.5px] ${warna} ${className}`}>{children}</p>
    );
}

/* Judul seksi 36px. */
export function JudulSeksi({ children, gelap = false, className = '', as: Tag = 'h2' }) {
    return (
        <Tag
            className={`!font-body text-[28px] font-semibold leading-tight tracking-[-1.2px] md:text-4xl ${
                gelap ? 'text-white' : 'text-pb-ink'
            } ${className}`}
        >
            {children}
        </Tag>
    );
}

/* Tautan teks dengan panah ke kanan, mis. "Jelajahi berita & artikel ->". */
export function TautanPanah({ ke, href, children, gelap = false, oranye = false, className = '' }) {
    const warna = oranye ? 'text-o-600' : gelap ? 'text-white' : 'text-pb-aksen';
    const kelas = `group inline-flex items-center gap-2 font-body text-sm font-semibold ${warna} hover:underline underline-offset-4 ${className}`;
    const isi = (
        <>
            {children}
            <Ikon nama="panah" className="size-4 transition-transform group-hover:translate-x-0.5" />
        </>
    );
    if (href) {
        return (
            <a href={href} className={kelas}>
                {isi}
            </a>
        );
    }
    return (
        <Link to={ke} className={kelas}>
            {isi}
        </Link>
    );
}

/*
 * Gambar yang, bila gagal dimuat atau alamatnya kosong, diganti ikon gambar di
 * tengah latar - tidak menampilkan ikon "gambar rusak" bawaan browser.
 */
export function GambarAman({ src, className = '', ikonClassName = 'size-8 text-pb-aksen/50' }) {
    const [gagal, setGagal] = useState(false);
    if (!src || gagal) {
        return (
            <span className={`flex items-center justify-center ${className}`}>
                <Ikon nama="gambar" className={ikonClassName} />
            </span>
        );
    }
    return <img src={src} alt="" loading="lazy" className={className} onError={() => setGagal(true)} />;
}

/* Kotak ilustrasi kartu: ikon di kiri, tiga garis motif di kanan. */
export function IlustrasiKartu({ ikon = 'buku', gambar, alt = '' }) {
    // Foto yang gagal dimuat (berkas unggahan hilang) jatuh kembali ke ilustrasi ikon.
    const [gagal, setGagal] = useState(false);
    if (gambar && !gagal) {
        return (
            <div className="h-28 overflow-hidden rounded-md bg-pb-kabut">
                <img src={gambar} alt={alt} className="size-full object-cover" loading="lazy" onError={() => setGagal(true)} />
            </div>
        );
    }
    return (
        <div className="flex h-28 items-center justify-between rounded-md bg-pb-kabut p-6">
            <Ikon nama={ikon} className="size-11 text-pb-aksen" />
            <span className="flex flex-col gap-2" aria-hidden="true">
                <span className="h-px w-[120px] bg-pb-motif" />
                <span className="h-px w-[88px] bg-pb-motif" />
                <span className="h-px w-14 bg-pb-motif" />
            </span>
        </div>
    );
}

/*
 * Kotak pencarian + tombol Cari + chip kategori (halaman Berita, Galeri, FAQ).
 * `kategori` berisi nama-nama chip; nilai null = "Semua". `tanpaCari` hanya
 * menampilkan chip (halaman FAQ punya kotak cari sendiri).
 */
export function BilahCari({ nilai, setNilai, onCari, placeholder, kategori = [], terpilih, setTerpilih, tanpaCari = false }) {
    return (
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
            {!tanpaCari && (
            <form
                className="flex w-full gap-4 xl:w-auto"
                onSubmit={(e) => {
                    e.preventDefault();
                    onCari();
                }}
            >
                <label className="flex h-12 flex-1 items-center gap-3 rounded-lg border border-pb-bingkai bg-white px-4 focus-within:border-pb-aksen xl:w-[320px] xl:flex-none">
                    <Ikon nama="cari" className="size-4 shrink-0 text-ink-500" />
                    <span className="sr-only">{placeholder}</span>
                    <input
                        type="search"
                        value={nilai}
                        onChange={(e) => setNilai(e.target.value)}
                        placeholder={placeholder}
                        className="w-full bg-transparent font-body text-sm text-pb-judul outline-none placeholder:text-ink-400"
                    />
                </label>
                <button
                    type="submit"
                    className="h-12 shrink-0 rounded-md bg-pb-tombol px-[22px] font-body text-sm font-semibold text-white hover:bg-g-700"
                >
                    Cari
                </button>
            </form>
            )}
            {kategori.length > 0 && (
                <div className="-mx-4 flex gap-1 overflow-x-auto px-4 [scrollbar-width:none] xl:mx-0 xl:px-0 [&::-webkit-scrollbar]:hidden" role="group" aria-label="Saring kategori">
                    {[null, ...kategori].map((k) => {
                        const aktif = (terpilih ?? null) === k;
                        return (
                            <button
                                key={k ?? 'semua'}
                                type="button"
                                aria-pressed={aktif}
                                onClick={() => setTerpilih(k)}
                                className={`inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 font-body text-[13px] font-medium transition-colors ${
                                    aktif ? 'bg-o-500 text-white' : 'text-pb-ink hover:bg-pb-kabut'
                                }`}
                            >
                                {aktif && <Ikon nama="centang" className="size-4" tebal={2} />}
                                {k ?? 'Semua'}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* Paginasi bulat 44px seperti di Figma. Halaman dihitung mulai 1. */
export function Paginasi({ halaman, jumlahHalaman, setHalaman }) {
    if (jumlahHalaman <= 1) return null;
    // Tampilkan paling banyak 5 nomor di sekitar halaman aktif.
    const awal = Math.max(1, Math.min(halaman - 2, jumlahHalaman - 4));
    const nomor = Array.from({ length: Math.min(5, jumlahHalaman) }, (_, i) => awal + i);
    const tombol = 'inline-flex size-11 items-center justify-center rounded-full border font-body text-sm';
    return (
        <nav className="flex items-center justify-center gap-2 pt-2" aria-label="Navigasi halaman">
            {nomor.map((n) => (
                <button
                    key={n}
                    type="button"
                    aria-current={n === halaman ? 'page' : undefined}
                    onClick={() => setHalaman(n)}
                    className={`${tombol} ${
                        n === halaman
                            ? 'border-g-600 bg-g-600 font-semibold text-white'
                            : 'border-pb-bingkai bg-white text-pb-ink hover:border-pb-aksen'
                    }`}
                >
                    {n}
                </button>
            ))}
        </nav>
    );
}

/* Teks polos dari isi HTML CMS, untuk ringkasan kartu. */
export function teksPolos(html = '') {
    // DOMParser, bukan innerHTML pada div: dokumen hasil parse tidak memuat gambar
    // atau menjalankan atribut onerror dari isi CMS.
    // Spasi disisipkan di batas paragraf/baris agar kalimat tidak menyambung.
    const doc = new DOMParser().parseFromString((html || '').replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, ' $&'), 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}
